import { enqueueSnackbar, OptionsWithExtraProps } from 'notistack';

import {
  ApolloCache,
  ApolloError,
  DefaultContext,
  DocumentNode,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  TypedDocumentNode,
  useMutation as useApolloMutation,
} from '@apollo/client';

import { safeJSONparse } from '../utils/json';



type Props = {
  messageFail?: string;
  messageSuccess?: string;
  refetch?: any;
};
interface SnackbarPropsExtended extends React.HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

export const useMutation = <
  TData = any,
  TVariables = OperationVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  {
    messageFail,
    messageSuccess = 'Done',
    refetch,
    update,
    ...options
  }: MutationHookOptions<TData, TVariables, TContext, TCache> & Props,
): MutationTuple<TData, TVariables, TContext, TCache> => {
  const executeMutation = async (
    variables: MutationFunctionOptions<TData, TVariables, TContext, ApolloCache<any>>,
  ) => {
    try {
      return (await handler(variables)).data;
    } catch (error) {
      handleApolloError(error, messageFail);
      return undefined;
    }
  };

  const [handler, result] = useApolloMutation(mutation, {
    onCompleted: (data) => {
      if (messageSuccess && typeof messageSuccess === 'string')
        enqueueSnackbar(messageSuccess, {
          autoHideDuration: 3000,
          SnackbarProps: {
            'data-testid': 'snackbar-success',
          } as SnackbarPropsExtended,
          variant: 'success',
        });
      if (typeof refetch === 'function') {
        refetch();
      }
    },
    onError: (error) => handleApolloError(error, messageFail),
    ...(typeof update === 'function' && { update }),
    ...options,
  });

  return [executeMutation, result];
};

const errorSnackBarOptions = {
  autoHideDuration: 3000,
  SnackbarProps: {
    'data-testid': 'snackbar-error',
  } as SnackbarPropsExtended,
  variant: 'error',
} as OptionsWithExtraProps<'error'>;

export function handleApolloError(error: any | ApolloError, messageFail) {
  const errors = safeJSONparse(error.graphQLErrors?.[0]?.message || error.errors?.[0]?.message);

  if (Array.isArray(errors) && errors.length) {
    for (const error of errors) {
      // handles multiple validation errors
      enqueueSnackbar(error.message, errorSnackBarOptions);
    }
  } else {
    const message = messageFail || error.message.substring(0, 300);
    if (typeof message === 'string') enqueueSnackbar(message, errorSnackBarOptions);
  }
}
